import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import PublicSeo from './components/PublicSeo';
import { isBlueBearAccessHostname } from './pages/access/accessHost';

const Landing = lazy(() => import('./pages/Landing'));
const ServiceAgents = lazy(() => import('./pages/ServiceAgents'));
const ServiceWhiteLabel = lazy(() => import('./pages/ServiceWhiteLabel'));
const ServiceHealth = lazy(() => import('./pages/ServiceHealth'));
const ServiceOptimization = lazy(() => import('./pages/ServiceOptimization'));
const ServiceDeploy = lazy(() => import('./pages/ServiceDeploy'));
const WordPressPlugin = lazy(() => import('./pages/WordPressPlugin'));
const Contact = lazy(() => import('./pages/Contact'));
const Insights = lazy(() => import('./pages/Insights'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Deploy = lazy(() => import('./pages/Deploy'));
const Integrations = lazy(() => import('./pages/Integrations'));
const Credits = lazy(() => import('./pages/Credits'));
const Models = lazy(() => import('./pages/Models'));
const AIAgents = lazy(() => import('./pages/AIAgents'));
const Workspaces = lazy(() => import('./pages/Workspaces'));
const Workflows = lazy(() => import('./pages/Workflows'));
const RetellWorkspaceAdmin = lazy(() => import('./pages/RetellWorkspaceAdmin'));
const AIChat = lazy(() => import('./pages/chat/ChatPage'));
const AISessions = lazy(() => import('./pages/AISessions'));
const Invocations = lazy(() => import('./pages/Invocations'));
const RoutingPolicies = lazy(() => import('./pages/RoutingPolicies'));
const WorkspaceMemoryAdmin = lazy(() => import('./pages/WorkspaceMemoryAdmin'));
const CloudManager = lazy(() => import('./pages/CloudManager'));
const WhiteLabelRequest = lazy(() => import('./pages/WhiteLabelRequest'));
const ComputeLinkLanding = lazy(() => import('./pages/ComputeLinkLanding'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const WhiteLabelDashboard = lazy(() => import('./pages/WhiteLabelDashboard'));
const Waitlist = lazy(() => import('./pages/Waitlist'));
const JoinWaitlist = lazy(() => import('./pages/JoinWaitlist'));
const Notifications = lazy(() => import('./pages/Notifications'));
const ManageUsers = lazy(() => import('./pages/ManageUsers'));
const TenantFeatures = lazy(() => import('./pages/TenantFeatures'));
const ReleaseManagement = lazy(() => import('./pages/ReleaseManagement'));
const UserDetails = lazy(() => import('./pages/UserDetails'));
const BillingUsage = lazy(() => import('./pages/BillingUsage'));
const BillingHistory = lazy(() => import('./pages/BillingHistory'));
const Features = lazy(() => import('./pages/Features'));
const PricingInfo = lazy(() => import('./pages/PricingInfo'));
const CaseStudies = lazy(() => import('./pages/CaseStudies'));
const GovernedExecutionProof = lazy(() => import('./pages/GovernedExecutionProof'));
const MarketplaceReview = lazy(() => import('./pages/marketplace/MarketplaceReview'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const MarketplaceResearch = lazy(() => import('./pages/MarketplaceResearch'));
const MarketplaceOffer = lazy(() => import('./pages/MarketplaceOffer'));
const MarketplaceSell = lazy(() => import('./pages/MarketplaceSell'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Security = lazy(() => import('./pages/Security'));
const CommercialPage = lazy(() => import('./pages/CommercialPage'));
const AuthorProfile = lazy(() => import('./pages/AuthorProfile'));
const BearClaw = lazy(() => import('./pages/BearClaw'));
const BearClawNews = lazy(() => import('./pages/BearClawNews'));
const BearClawPost = lazy(() => import('./pages/BearClawPost'));
const Videos = lazy(() => import('./pages/Videos'));
const VideoWatch = lazy(() => import('./pages/VideoWatch'));
const UseOffer = lazy(() => import('./pages/UseOffer'));
const PlatformApp = lazy(() => import('./components/PlatformApp'));
const BlueBearAccess = lazy(() => import('./pages/access/BlueBearAccess'));

function RouteFallback() {
  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      <div className="mx-auto flex min-h-screen max-w-6xl items-center justify-center px-6">
        <div className="rounded-3xl border border-gray-800 bg-gray-900/70 px-6 py-5 text-sm uppercase tracking-[0.24em] text-gray-400">
          Loading page
        </div>
      </div>
    </div>
  );
}

function AuthPageShell({ children }) {
  return (
    <AuthProvider>
      <>
        {children}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              // Token variables rather than literals: toasts render in a portal
              // on <body>, so they inherit :root and follow the theme.
              background: 'rgb(var(--c-gray-800))',
              color: 'rgb(var(--c-gray-100))',
              borderRadius: '0.5rem',
            },
          }}
        />
      </>
    </AuthProvider>
  );
}

function App() {
  const hostname = typeof window === 'undefined' ? '' : window.location.hostname.toLowerCase();
  const isAccessSite = isBlueBearAccessHostname(hostname);

  if (isAccessSite) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <BlueBearAccess />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <PublicSeo />
      <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/services/agents" element={<ServiceAgents />} />
          <Route path="/services/white-label" element={<ServiceWhiteLabel />} />
          <Route path="/services/health" element={<ServiceHealth />} />
          <Route path="/services/optimization" element={<ServiceOptimization />} />
          <Route path="/services/deploy" element={<ServiceDeploy />} />
          <Route path="/wordpress" element={<WordPressPlugin />} />
          <Route path="/features" element={<Features />} />
          <Route path="/services" element={<CommercialPage pageId="services" />} />
          <Route path="/mcp-gateway" element={<CommercialPage pageId="mcpGateway" />} />
          <Route path="/about" element={<CommercialPage pageId="about" />} />
          {/* Static, so it outranks the `/:slug` article route below regardless of
              declaration order — but declared beside /about because that is where it
              belongs when you are reading this file. */}
          <Route path="/phillip-joe" element={<AuthorProfile />} />
          <Route path="/mcp-2026-enterprise-migration-guide" element={<CommercialPage pageId="mcpMigration2026" />} />
          <Route path="/ai-agent-identity-authorization" element={<CommercialPage pageId="agentIdentity" />} />
          <Route path="/agentic-ai-security-owasp-control-map" element={<CommercialPage pageId="owaspControlMap" />} />
          <Route path="/ai-agent-cost-per-outcome-calculator" element={<CommercialPage pageId="costCalculator" />} />
          <Route path="/bearclaw" element={<BearClaw />} />
          <Route path="/bearclaw/news" element={<BearClawNews />} />
          <Route path="/bearclaw/news/:slug" element={<BearClawPost />} />
          <Route path="/pricing-info" element={<PricingInfo />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/governed-agent-execution-proof" element={<GovernedExecutionProof />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/marketplace/research" element={<MarketplaceResearch />} />
          <Route path="/marketplace/:offerType" element={<MarketplaceOffer />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/security" element={<Security />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/contact-us" element={<Navigate to="/contact" replace />} />
          <Route path="/join-waitlist" element={<JoinWaitlist />} />
          <Route path="/waitlist" element={<Navigate to="/join-waitlist" replace />} />
          <Route path="/schedule-a-call-today" element={<Navigate to="/contact" replace />} />
          <Route path="/about-us" element={<Navigate to="/about" replace />} />
          <Route path="/docs" element={<Navigate to="/insights" replace />} />
          <Route path="/category/:category" element={<Navigate to="/insights" replace />} />
          <Route path="/tag/:tag" element={<Navigate to="/insights" replace />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/videos" element={<Videos />} />
          <Route path="/videos/:slug" element={<VideoWatch />} />
          <Route path="/use/:slug" element={<UseOffer />} />
          <Route path="/:slug" element={<BlogPost />} />
          <Route path="/compute" element={<ComputeLinkLanding />} />
          <Route path="/login" element={<AuthPageShell><Login /></AuthPageShell>} />
          <Route path="/register" element={<AuthPageShell><Register /></AuthPageShell>} />
          <Route path="/forgot-password" element={<AuthPageShell><ForgotPassword /></AuthPageShell>} />
          <Route path="/reset-password" element={<AuthPageShell><ResetPassword /></AuthPageShell>} />
          <Route path="/verify-email" element={<AuthPageShell><VerifyEmail /></AuthPageShell>} />
          <Route element={<PlatformApp />}>
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/marketplace/sell" element={<MarketplaceSell />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/deploy" element={<Deploy />} />
                <Route path="/integrations" element={<Integrations />} />
                <Route path="/credits" element={<Credits />} />
                <Route path="/credits/usage" element={<BillingUsage />} />
                <Route path="/credits/history" element={<BillingHistory />} />
                <Route path="/models" element={<Models />} />
                <Route path="/workspaces" element={<Workspaces />} />
                <Route path="/workspaces/:workspaceId/retell" element={<RetellWorkspaceAdmin />} />
                <Route path="/workflows/*" element={<Workflows />} />
                <Route path="/ai-agents" element={<AIAgents />} />
                <Route path="/ai-agents/:agentId" element={<AIAgents />} />
                <Route path="/voice-infrastructure" element={<AIAgents initialView="voice-infrastructure" />} />
                <Route path="/ai-chat" element={<AIChat />} />
                <Route path="/ai-sessions" element={<AISessions />} />
                <Route path="/invocations" element={<Invocations />} />
                <Route path="/routing-rules" element={<RoutingPolicies />} />
                <Route path="/marketplace/review" element={<MarketplaceReview />} />
                <Route path="/admin/routing-policies" element={<RoutingPolicies admin />} />
                <Route path="/admin/workspace-memory" element={<WorkspaceMemoryAdmin />} />
                <Route path="/cloud-manager/*" element={<CloudManager />} />
                <Route path="/white-label-request" element={<WhiteLabelRequest />} />
                <Route path="/white-label" element={<WhiteLabelDashboard />} />
                <Route path="/white-label/*" element={<WhiteLabelDashboard />} />
                <Route path="/billing" element={<Navigate to="/credits" replace />} />
                <Route path="/billing/usage" element={<Navigate to="/credits/usage" replace />} />
                <Route path="/billing/history" element={<Navigate to="/credits/history" replace />} />
                <Route path="/admin/waitlist" element={<Waitlist />} />
                <Route path="/admin/notifications" element={<Notifications />} />
                <Route path="/admin/users" element={<ManageUsers />} />
                <Route path="/admin/tenant-features" element={<TenantFeatures />} />
                <Route path="/admin/releases" element={<ReleaseManagement />} />
                <Route path="/admin/users/:userId" element={<UserDetails />} />
              </Route>
            </Route>
          </Route>
      </Routes>
    </Suspense>
  );
}

export default App;
